import { Client, ClientAccessSettingKey } from '@models/client.model';
import { graphQlClient, graphQlUpload } from '@config/graphqlClient';
import ClientsGraphQL from '@graphql/client.queries';

export type CreateClientPayload = Omit<
  Client,
  'clientId' | 'projects' | 'logo'
> & {
  projectIds?: string[];
  logo?: File | null;
};

export type UpdateClientPayload = Omit<Client, 'projects' | 'logo'> & {
  projectIds?: string[];
  logo?: File | null;
};

export const clientService = {
  async getClients(): Promise<Client[]> {
    const data: any = await graphQlClient.request(
      ClientsGraphQL.queries.getClients,
    );
    return data.getClients as Client[];
  },

  async getClientById(clientId: string): Promise<Client> {
    if (!clientId) throw new Error('clientId is required');
    const data: any = await graphQlClient.request(
      ClientsGraphQL.queries.getClientByID,
      { id: clientId },
    );
    if (!data?.getClientByID) throw new Error('Client not found');
    return data.getClientByID as Client;
  },

  async createClient(payload: CreateClientPayload) {
    const clientInput: any = {
      name: payload.name,
      address: payload.address,
      phone: payload.phone,
      email: payload.email,
      projectIds: payload.projectIds || [],
    };

    if (payload.logo) {
      const data: any = await graphQlUpload(
        payload.logo,
        'client',
        ClientsGraphQL.mutations.createClient,
        { ...clientInput },
      );
      return data.createClient as Client;
    }

    const data: any = await graphQlClient.request(
      ClientsGraphQL.mutations.createClient,
      { client: clientInput },
    );
    return data.createClient as Client;
  },

  async updateClient(payload: UpdateClientPayload) {
    if (!payload.clientId) throw new Error('clientId is required');
    const clientInput: any = {
      clientId: payload.clientId,
      name: payload.name,
      address: payload.address,
      phone: payload.phone,
      email: payload.email,
      projectIds: payload.projectIds || [],
    };

    if (payload.logo) {
      const data: any = await graphQlUpload(
        payload.logo,
        'client',
        ClientsGraphQL.mutations.updateClient,
        { ...clientInput },
      );
      return data.updateClient as Client;
    }

    const data: any = await graphQlClient.request(
      ClientsGraphQL.mutations.updateClient,
      { client: clientInput },
    );
    return data.updateClient as Client;
  },

  async deleteClient(clientId: string) {
    if (!clientId) throw new Error('clientId is required');
    const data: any = await graphQlClient.request(
      ClientsGraphQL.mutations.deleteClient,
      { id: clientId },
    );
    return data.deleteClient;
  },

  // Sprint 25, story-128 (run 5). Used for the initial invite (no linked user yet), Flow C's
  // resend (`resend: true`), and Flow D's "Replace login" (invite the new email after the old
  // membership is disabled by `removeClientAccess`).
  async inviteClientUser(
    clientId: string,
    resend?: boolean,
  ): Promise<Client> {
    if (!clientId) throw new Error('clientId is required');
    const data: any = await graphQlClient.request(
      ClientsGraphQL.mutations.inviteClientUser,
      { clientId, resend: !!resend },
    );
    return data.inviteClientUser as Client;
  },

  async removeClientAccess(clientId: string): Promise<Client> {
    if (!clientId) throw new Error('clientId is required');
    const data: any = await graphQlClient.request(
      ClientsGraphQL.mutations.removeClientAccess,
      { clientId },
    );
    return data.removeClientAccess as Client;
  },

  async updateClientAccessSetting(
    clientId: string,
    key: ClientAccessSettingKey,
    value: boolean,
  ): Promise<Client> {
    if (!clientId) throw new Error('clientId is required');
    const data: any = await graphQlClient.request(
      ClientsGraphQL.mutations.updateClientAccessSetting,
      { clientId, key, value },
    );
    return data.updateClientAccessSetting as Client;
  },
};

export default clientService;
